<template>
  <AuthLayout>
    <template #eyebrow>ScrapingAnt</template>
    <template #headline>Welcome back.</template>
    <template #subhead>Pick up where you left off.</template>

    <template #features>
      <li class="auth-feature">
        <img src="/auth/icon-js.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">MCP server</p>
          <p class="auth-feature__body">
            Plug ScrapingAnt into Claude and other AI agents.
          </p>
        </div>
      </li>
      <li class="auth-feature">
        <img src="/auth/icon-proxies.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">Residential + datacenter proxies</p>
          <p class="auth-feature__body">
            Switch pools per request for any target.
          </p>
        </div>
      </li>
      <li class="auth-feature">
        <img src="/auth/icon-bypass.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">IP whitelisting</p>
          <p class="auth-feature__body">
            Lock API access to your infrastructure.
          </p>
        </div>
      </li>
    </template>

    <template #footnote>
      All systems operational ·
      <a href="https://docs.scrapingant.com" target="_blank">Docs</a>
      ·
      <a href="mailto:support@scrapingant.com">Support</a>
    </template>

    <template #mobileBanner>
      <p class="auth-mobile-banner__eyebrow">ScrapingAnt</p>
      <p class="auth-mobile-banner__headline">Welcome back.</p>
    </template>

    <template #form>
      <v-card class="auth-card elevation-2">
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
        />

        <div class="auth-card__head">
          <h2 class="auth-card__title" v-if="!unconfirmed && !ip_whitelist_blocked">
            Log in
          </h2>
          <p
            class="auth-card__subtitle"
            v-if="!unconfirmed && !ip_whitelist_blocked"
          >
            Access your ScrapingAnt dashboard.
          </p>
        </div>

        <SocialLoginButtons
          v-if="!unconfirmed && !ip_whitelist_blocked"
          class="auth-card__social"
          @ip-whitelist-blocked="ip_whitelist_blocked = true"
        />

        <div
          v-if="!unconfirmed && !ip_whitelist_blocked"
          class="auth-card__divider"
        >
          <span>or log in with email</span>
        </div>

        <div v-if="unconfirmed" class="auth-card__head">
          <h2 class="auth-card__title">Confirm your email</h2>
          <p class="auth-card__subtitle">
            Please check your email
            <b>{{ form.body.email }}</b> for the confirmation letter.
          </p>
          <p class="auth-card__subtitle" style="margin-top: 12px">
            <b>Didn't receive the email?</b> Check your spam folder or click
            <b>Resend email</b> below.
          </p>
        </div>

        <div v-if="unconfirmed" class="auth-card__actions">
          <v-btn color="primary" @click="reset_state">Login</v-btn>
          <v-btn :disabled="confirmation_resent" @click="resend"
            >Resend email</v-btn
          >
        </div>

        <div v-if="ip_whitelist_blocked" class="auth-card__head">
          <h2 class="auth-card__title">Access blocked</h2>
        </div>
        <div v-if="ip_whitelist_blocked" class="auth-card__blocked">
          <IpWhitelistBlockedMessage />
        </div>
        <div v-if="ip_whitelist_blocked" class="auth-card__actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="reset_state">Back to login</v-btn>
        </div>

        <div
          v-if="confirmation_resent"
          class="confirmation-message"
          style="margin-top: 20px"
        >
          <div>
            Confirmation email has been resent. Please contact us at
            <a href="mailto:support@scrapingant.com">support@scrapingant.com</a>
            if the email was not received.
          </div>
        </div>

        <div
          v-if="!unconfirmed && !ip_whitelist_blocked"
          class="auth-card__form"
          v-on:keyup.enter="authEnter"
        >
          <v-text-field
            v-model="$v.form.body.email.$model"
            :error-messages="emailErrors"
            @input="$v.form.body.email.$touch()"
            @blur="$v.form.body.email.$touch()"
            autofocus
            required
            label="E-mail"
            class="mb-2"
          ></v-text-field>

          <v-text-field
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showPassword ? 'text' : 'password'"
            v-model="$v.form.body.password.$model"
            :error-messages="passwordErrors"
            required
            @input="$v.form.body.password.$touch()"
            @blur="$v.form.body.password.$touch()"
            @click:append="showPassword = !showPassword"
            label="Password"
          ></v-text-field>

          <div class="auth-card__forgot">
            <router-link :to="{ name: 'ResetPassword' }"
              >Forgot password?</router-link
            >
          </div>

          <vue-recaptcha
            :sitekey="constants.RECAPTCHA_SITE_KEY"
            @verify="captcha_verify"
          ></vue-recaptcha>

          <span class="form-error">{{ form.error }}</span>
        </div>

        <div v-if="!unconfirmed && !ip_whitelist_blocked" class="auth-card__cta">
          <v-btn
            color="primary"
            block
            large
            @click="auth"
            :disabled="isDisabledForm"
          >Log in</v-btn>
        </div>

        <div v-if="!unconfirmed && !ip_whitelist_blocked" class="auth-card__switch">
          New to ScrapingAnt?
          <router-link :to="{ name: 'Signup' }">Create free account</router-link>
        </div>

        <div class="loading-overlay" v-if="loading">
          <v-progress-circular
            :size="50"
            :width="2"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-card>
    </template>
  </AuthLayout>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import constants from "@/config/constants";
import SocialLoginButtons from "@/views/SocialLoginButtons";
import AuthLayout from "@/components/auth/AuthLayout";
import IpWhitelistBlockedMessage from "@/components/IpWhitelistBlockedMessage";
import { mdiEyeOff } from "@mdi/js";
import { VueRecaptcha } from "vue-recaptcha";
import { getCookie } from "@/utils/common_utils";
export default {
  name: "Login",
  components: {
    VueRecaptcha,
    SocialLoginButtons,
    AuthLayout,
    IpWhitelistBlockedMessage,
  },
  data() {
    return {
      constants,
      showPassword: false,
      Passwordicon: mdiEyeOff,
      loading: false,
      unconfirmed: false,
      ip_whitelist_blocked: false,
      confirmation_resent: false,
      captcha_response: null,
      form: {
        body: {
          email: "",
          password: "",
        },
        error: "",
      },
    };
  },
  validations: {
    form: {
      body: {
        email: {
          required,
          email,
        },
        password: {
          required,
        },
      },
    },
  },
  computed: {
    passwordErrors() {
      const errors = [];
      if (!this.$v.form.body.password.$dirty) return errors;
      !this.$v.form.body.password.required &&
        errors.push("Please enter your password");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.form.body.email.$dirty) return errors;
      !this.$v.form.body.email.email &&
        errors.push("Please enter a valid email");
      !this.$v.form.body.email.required &&
        errors.push("Please enter a valid email");
      return errors;
    },
    isDisabledForm() {
      return (
        this.$v.form.body.email.$error ||
        this.$v.form.body.password.$error ||
        !this.$v.form.body.email.$dirty ||
        !this.$v.form.body.password.$dirty ||
        !this.captcha_response
      );
    },
  },
  methods: {
    errors(res) {
      this.form.error =
        res?.data?.error?.msg ?? "Error occurred. Please, try again.";
    },
    auth() {
      this.$gtag.event(constants.ANALYTICS_EVENTS.LOGIN, {
        method: "general",
      });
      const userSource = getCookie("user_source");
      this.loading = true;
      this.$store
        .dispatch("auth/login", {
          ...this.form.body,
          user_source: userSource ? userSource : "direct",
          "recaptcha-token": this.captcha_response,
        })
        .then(
          () => {
            this.loading = false;
          },
          (err) => {
            this.loading = false;

            if (err?.response?.status === 403) {
              const errorMsg = err?.response?.data?.error?.msg || "";
              if (
                errorMsg.includes("Access denied") &&
                errorMsg.includes("IP address")
              ) {
                this.ip_whitelist_blocked = true;
              } else if (errorMsg.includes("email is not verified")) {
                this.unconfirmed = true;
              } else {
                this.errors(err.response);
              }
            } else {
              this.errors(err.response);
            }
          }
        );
    },
    authEnter() {
      if (!this.isDisabledForm) {
        this.auth();
      }
    },
    reset_state() {
      this.unconfirmed = false;
      this.ip_whitelist_blocked = false;
      this.confirmation_resent = false;
      this.form.error = "";
    },
    captcha_verify(response) {
      this.captcha_response = response;
    },
    resend() {
      this.$gtag.event(constants.ANALYTICS_EVENTS.RESEND_EMAIL, {
        event_category: constants.ANALYTICS_EVENTS.DEFAULT_CATEGORY,
      });
      this.loading = true;
      this.$store.dispatch("auth/resend_email", this.form.body.email).then(
        () => {
          this.loading = false;
          this.confirmation_resent = true;
        },
        (res) => {
          this.loading = false;
          this.errors(res.response);
        }
      );
    },
  },
};
</script>

<style scoped lang="scss">
.auth-card {
  position: relative;
  width: 100%;
  max-width: 440px;
  padding: 40px 36px 32px;
  border-radius: 12px;
}

.auth-card__head {
  text-align: center;
  margin-bottom: 24px;
}

.auth-card__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  color: #0f172a;
  margin: 0 0 8px;
}

.auth-card__subtitle {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(15, 23, 42, 0.68);
  margin: 0;
}

.auth-card__social {
  margin-bottom: 20px;
}

.auth-card__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0 16px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(15, 23, 42, 0.12);
  }
}

.auth-card__form {
  display: flex;
  flex-direction: column;
}

.auth-card__forgot {
  display: flex;
  justify-content: flex-end;
  margin: 4px 0 16px;

  a {
    font-size: 13px;
    color: #e2782f;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.auth-card__cta {
  margin-top: 8px;
}

.auth-card__switch {
  margin-top: 20px;
  text-align: center;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.7);

  a {
    color: #e2782f;
    font-weight: 500;
    text-decoration: none;
    margin-left: 4px;
  }
}

.auth-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.auth-card__blocked {
  margin-bottom: 8px;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.78);

  ::v-deep a {
    color: #e2782f;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.form-error {
  color: #d32f2f;
  font-size: 13px;
  margin-top: 8px;
  display: block;
  min-height: 18px;
}
</style>
